import { isValidElement, ReactNode } from "react";

import _ from "lodash";

import { ExecutionTypeEnum, EnvironmentDeploymentStrategyEnum } from "@/__generated__/graphql";
import { ApplicationActions } from "@/components/Actions/ApplicationActions";
import { BuildActions } from "@/components/Actions/BuildActions";
import { EnvironmentActions } from "@/components/Actions/EnvironmentActions";
import EnvironmentSubtitle from "@/components/Environments/EnvironmentSubtitle";
import PodStateBadge from "@/components/PodStateBadge";
import { HeaderTab } from "@/components/shared/HeaderTabs";
import { SidebarMenuItem } from "@/components/SidebarMenu";
import BuildStateBadge from "@/components/StateBadges/BuildStateBadge";
import EnvironmentDeployStateBadge from "@/components/StateBadges/EnvironmentDeployStateBadge";
import EnvironmentStateBadge from "@/components/StateBadges/EnvironmentStateBadge";
import NamespaceStateBadge from "@/components/StateBadges/NamespaceStateBadge";
import { LearnMoreEntryKey } from "@/constants/learnMore";
import * as navMenus from "@/constants/navMenus";
import { NavMenuItem, NavMenuItemSlug } from "@/constants/navMenus";
import * as sidebarMenus from "@/constants/sidebarMenus";
import { SETTINGS_MENU_WIDTH } from "@/constants/sizes";
import * as tabs from "@/constants/tabs";
import { TabItem } from "@/constants/tabs";
import { LayoutDataProviderType } from "@/providers/useLayoutData";
import { RecursivePartial } from "@/types/recursivePartial";
import featureFlagEnabled from "@/utils/featureFlagEnabled";
import { applicationPath, environmentPath, jobPath, membersPath, secretsPath } from "@/utils/pathGenerators";

type P = RecursivePartial<LayoutMode>;

const defaults: LayoutMode = {
  learnMoreContent: [],
  header: {
    breadcrumbsOmitIndexList: [0, 1],
  },
  navMenu: {
    selectedItem: "applications" as const,
    topNavMenuItems: navMenus.releaseTopNavMenuItems,
    bottomNavMenuItems: navMenus.releaseBottomNavMenuItems,
  },
  sidebarMenu: {
    menuItems: [],
  },
  extend: function () {
    return this;
  },
};

function mergeCustomizer(objValue: unknown, srcValue: unknown) {
  if (_.isArray(objValue) || isValidElement(objValue)) {
    return srcValue;
  }
}
const merge = (dest: P, ...src: P[]): LayoutMode => {
  return _.mergeWith(dest, ...src, mergeCustomizer) as LayoutMode;
};
const mergeDefaults = (src: P): LayoutMode => {
  return merge({}, defaults, src);
};

const configurationMode = mergeDefaults({
  header: {
    breadcrumbsOmitIndexList: [0, 1, 2],
  },
  navMenu: {
    selectedItem: "configuration" as const,
  },
  sidebarMenu: {
    heading: "Configuration",
    menuItems: sidebarMenus.configurationMenuItems,
    basePath: "/workspaces/[workspaceId]/configuration",
  },
});

const agentsMode = mergeDefaults({
  header: {
    breadcrumbsOmitIndexList: [0, 1, 2],
  },
  navMenu: {
    selectedItem: "agents" as const,
  },
  sidebarMenu: {
    heading: "Managed Agents",
    menuItems: sidebarMenus.agentsMenuItems,
    basePath: "/workspaces/[workspaceId]/agents",
  },
});

const settingsMode = mergeDefaults({
  header: {
    breadcrumbsOmitIndexList: [0, 1, 2],
  },
  navMenu: {
    selectedItem: "settings" as const,
  },
  sidebarMenu: {
    heading: "Settings",
    menuItems: sidebarMenus.settingsMenuItems,
    basePath: "/workspaces/[workspaceId]/settings",
  },
});

export const layoutModes = {
  default: defaults,
  ai: mergeDefaults({
    header: {
      breadcrumbsOmitIndexList: [0, 1, 2, 5, 7],
    },
    navMenu: {
      selectedItem: "chat" as const,
      topNavMenuItems: navMenus.aiTopNavMenuItems,
      bottomNavMenuItems: navMenus.aiBottomNavMenuItems,
    },
  }),
  configuration: configurationMode,
  agents: agentsMode,
  nodeGroupCreate: merge({}, configurationMode, {
    header: {
      breadcrumbsOmitIndexList: [0, 1, 2, 5],
    },
  }),
  secrets: merge({}, configurationMode, {
    header: {
      tabs: tabs.secretTabs,
    },
    extend: function ({ layoutWorkspace }: LayoutDataProviderType) {
      return merge(this, {
        header: {
          basePath:
            layoutWorkspace &&
            secretsPath({
              workspaceId: layoutWorkspace.databaseId,
            }),
        },
      });
    },
  }),
  delivery: mergeDefaults({
    header: {
      breadcrumbsOmitIndexList: [0, 1, 2],
    },
    navMenu: {
      selectedItem: "delivery" as const,
    },
  }),
  instantDatasets: mergeDefaults({
    header: {
      breadcrumbsOmitIndexList: [0, 1, 2, 5, 7],
    },
    navMenu: {
      selectedItem: "datasets" as const,
      topNavMenuItems: navMenus.instantDatasetsTopNavMenuItems,
      bottomNavMenuItems: navMenus.instantDatasetsBottomNavMenuItems,
    },
  }),
  settings: settingsMode,
  members: merge({}, settingsMode, {
    // The members index now shows both the member list and pending invitations
    // on one page, so there is no separate "Invites" tab — no header tab strip.
    extend: function ({ layoutWorkspace }: LayoutDataProviderType) {
      return merge(this, {
        header: {
          basePath:
            layoutWorkspace &&
            membersPath({
              workspaceId: layoutWorkspace.databaseId,
            }),
        },
      });
    },
  }),
  application: mergeDefaults({
    header: {
      tabs: tabs.serverApplicationTabs,
    },
    extend: function ({ layoutWorkspace, layoutApplication }: LayoutDataProviderType) {
      let includeBuilds = true;
      const excludeBuildsFromApp = featureFlagEnabled(layoutApplication, "per_env_builds");
      if (excludeBuildsFromApp) {
        includeBuilds = false;
      }
      const excludeBuildsFromWorkspace = featureFlagEnabled(layoutWorkspace, "per_env_builds");
      if (excludeBuildsFromWorkspace) {
        includeBuilds = false;
      }

      const hasEnvironments = (layoutApplication?.environments?.totalCount ?? 0) > 0;

      const executionType = layoutApplication?.executionType;
      const filteredTabs = tabs.serverApplicationTabs.filter(tab => {
        switch (tab.slug) {
          case "builds":
            return includeBuilds;
          default:
            return true;
        }
      });

      const processedTabs = filteredTabs.map(tab => {
        const isDisabled =
          (tab.slug === "builds" && (!includeBuilds || !hasEnvironments)) ||
          (tab.slug === "deploys" && !hasEnvironments);

        return isDisabled ? { ...tab, path: undefined, isDisabled: true } : tab;
      });

      return merge(this, {
        titleSuffix: layoutApplication?.name,
        header: {
          tabs: executionType === ExecutionTypeEnum.Runnable ? tabs.runnableApplicationTabs : processedTabs,
          heading: layoutApplication?.name,
          actions: (
            <ApplicationActions application={layoutApplication} hideCreateEnvironment={!hasEnvironments} />
          ),
          basePath:
            layoutWorkspace &&
            layoutApplication &&
            applicationPath({
              workspaceId: layoutWorkspace.databaseId,
              applicationId: layoutApplication.databaseId,
            }),
        },
      });
    },
  }),
  applicationClone: mergeDefaults({
    extend: function ({ layoutWorkspace, layoutApplication }: LayoutDataProviderType) {
      return merge(this, {
        titleSuffix: layoutApplication?.name,
        header: {
          heading: `Clone ${layoutApplication?.name}`,
          basePath:
            layoutWorkspace &&
            layoutApplication &&
            applicationPath({
              workspaceId: layoutWorkspace.databaseId,
              applicationId: layoutApplication.databaseId,
            }),
        },
      });
    },
  }),
  environment: mergeDefaults({
    header: {
      tabs: tabs.environmentTabs,
    },
    extend: function ({ layoutWorkspace, layoutApplication, layoutEnvironment }: LayoutDataProviderType) {
      const includeNamespaces =
        layoutEnvironment?.deploymentStrategy === EnvironmentDeploymentStrategyEnum.Rainbow;

      let includeBuilds = true;
      const excludeBuildsFromApp = featureFlagEnabled(layoutApplication, "per_env_builds");
      if (excludeBuildsFromApp) {
        includeBuilds = false;
      }
      const excludeBuildsFromWorkspace = featureFlagEnabled(layoutWorkspace, "per_env_builds");
      if (excludeBuildsFromWorkspace) {
        includeBuilds = false;
      }

      const filteredTabs = tabs.environmentTabs.filter(tab => {
        switch (tab.slug) {
          case "namespaces":
            return includeNamespaces;
          case "builds":
            return includeBuilds;
          case "deploys":
          case "app_imports":
          default:
            return true;
        }
      });
      const latestPipeline = layoutEnvironment?.pipelines?.nodes?.[0];
      return merge(this, {
        titleSuffix: [layoutApplication?.name, layoutEnvironment?.name].join("/"),
        header: {
          tabs: filteredTabs,
          actions: <EnvironmentActions environment={layoutEnvironment} />,
          heading: layoutEnvironment?.name,
          subheading: <EnvironmentSubtitle environment={layoutEnvironment} fontSize="md" />,
          stateBadge: (
            <EnvironmentStateBadge
              environment={layoutEnvironment}
              statusReason={latestPipeline?.statusReason}
            />
          ),
          basePath:
            layoutWorkspace &&
            layoutApplication &&
            layoutEnvironment &&
            environmentPath({
              workspaceId: layoutWorkspace.databaseId,
              applicationId: layoutApplication.databaseId,
              environmentId: layoutEnvironment.databaseId,
            }),
        },
      });
    },
  }),
  build: mergeDefaults({
    extend: function ({ layoutApplication, layoutEnvironment, layoutBuild }: LayoutDataProviderType) {
      return merge(this, {
        titleSuffix: _.compact([layoutApplication?.name, layoutEnvironment?.name]).join("/"),
        header: {
          actions: layoutBuild && <BuildActions build={layoutBuild} />,
          heading: layoutBuild && `Build ${layoutBuild.unhashedDatabaseId}`,
          stateBadge: <BuildStateBadge state={layoutBuild?.state} />,
        },
      });
    },
  }),
  deploy: mergeDefaults({
    extend: function ({ layoutApplication, layoutEnvironment, layoutDeploy }: LayoutDataProviderType) {
      return merge(this, {
        titleSuffix: _.compact([layoutApplication?.name, layoutEnvironment?.name]).join("/"),
        header: {
          stateBadge: (
            <EnvironmentDeployStateBadge
              state={layoutDeploy?.state}
              statusReason={layoutDeploy?.statusReason}
            />
          ),
          heading: layoutDeploy && `${layoutDeploy.name || "Deploy"} ${layoutDeploy.unhashedDatabaseId}`,
          subheading: <EnvironmentSubtitle environment={layoutEnvironment} fontSize="md" />,
        },
      });
    },
  }),
  instance: mergeDefaults({
    extend: function ({
      layoutApplication,
      layoutEnvironment,
      layoutEnvironmentInstance,
    }: LayoutDataProviderType) {
      return merge(this, {
        titleSuffix: _.compact([layoutApplication?.name, layoutEnvironment?.name]).join("/"),
        header: {
          stateBadge: <PodStateBadge state={layoutEnvironmentInstance?.currentStatus} />,
          heading: layoutEnvironmentInstance?.name,
          subheading: <EnvironmentSubtitle environment={layoutEnvironment} fontSize="md" />,
        },
      });
    },
  }),
  job: mergeDefaults({
    header: {
      tabs: tabs.jobTabs,
    },
    extend: function ({
      layoutWorkspace,
      layoutApplication,
      layoutEnvironment,
      layoutDeploy,
    }: LayoutDataProviderType) {
      return merge(this, {
        titleSuffix: [layoutApplication?.name, layoutEnvironment?.name].join("/"),
        header: {
          heading: layoutEnvironment?.name,
          subheading: <EnvironmentSubtitle environment={layoutEnvironment} fontSize="md" />,
          basePath:
            layoutWorkspace &&
            layoutApplication &&
            layoutEnvironment &&
            jobPath({
              workspaceId: layoutWorkspace.databaseId,
              applicationId: layoutApplication.databaseId,
              environmentId: layoutEnvironment.databaseId,
            }),
        },
      });
    },
  }),
  applicationSettings: mergeDefaults({
    sidebarMenu: {
      basePath: "/workspaces/[workspaceId]/applications/[applicationId]/settings",
      heading: "Settings",
      width: SETTINGS_MENU_WIDTH,
      menuItems: sidebarMenus.applicationSettingsMenuItems,
    },
  }),
  environmentSettings: mergeDefaults({
    sidebarMenu: {
      basePath:
        "/workspaces/[workspaceId]/applications/[applicationId]/environments/[environmentId]/settings",
      heading: "Settings",
      width: SETTINGS_MENU_WIDTH,
      menuItems: sidebarMenus.environmentSettingsMenuItems,
    },
  }),
  jobSettings: mergeDefaults({
    sidebarMenu: {
      basePath: "/workspaces/[workspaceId]/applications/[applicationId]/jobs/[environmentId]/settings",
      heading: "Settings",
      width: SETTINGS_MENU_WIDTH,
      menuItems: sidebarMenus.jobSettingsMenuItems,
    },
  }),
  namespace: mergeDefaults({
    extend: function ({ layoutNamespace }: LayoutDataProviderType) {
      return merge(this, {
        titleSuffix: _.compact(["Namespace", layoutNamespace?.name]).join("/"),
        header: {
          heading: layoutNamespace?.name,
          stateBadge: <NamespaceStateBadge state={layoutNamespace?.state} />,
        },
      });
    },
  }),
  onboarding: mergeDefaults({
    header: {
      breadcrumbsOmitIndexList: [0, 1, 2, 3, 4],
    },
  }),
  wizard: mergeDefaults({
    extend: function ({ layoutWorkspace, layoutApplication }: LayoutDataProviderType) {
      return merge(this, {
        titleSuffix: layoutApplication?.name,
        header: {
          heading: "Create Environment",
          basePath:
            layoutWorkspace &&
            layoutApplication &&
            applicationPath({
              workspaceId: layoutWorkspace.databaseId,
              applicationId: layoutApplication.databaseId,
            }),
        },
      });
    },
  }),
};

export interface ModeNavMenuProps {
  selectedItem?: NavMenuItemSlug;
  topNavMenuItems: ReadonlyArray<NavMenuItem>;
  bottomNavMenuItems: ReadonlyArray<NavMenuItem>;
}

export interface ModeSidebarMenuProps {
  heading?: string;
  menuItems: ReadonlyArray<SidebarMenuItem | string>;
  width?: string | number;
  basePath?: string;
}

export interface ModeHeaderProps {
  heading?: ReactNode;
  subheading?: ReactNode;
  stateBadge?: ReactNode;
  actions?: ReactNode;
  selectedTab?: TabItem;
  tabs?: ReadonlyArray<HeaderTab<string>>;
  basePath?: string;
  breadcrumbsOmitIndexList: ReadonlyArray<number>;
}

export interface LayoutMode {
  titleSuffix?: string;
  learnMoreContent: LearnMoreEntryKey[];
  header: ModeHeaderProps;
  navMenu: ModeNavMenuProps;
  sidebarMenu: ModeSidebarMenuProps;
  extend: (layoutData: LayoutDataProviderType) => LayoutMode;
}
